import { Badge, BadgeProps, tokens } from "@blurb/codex-react";
import { Box, createStyles } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";
import { GraphQL } from "@rpi/openapi-api";
import React from "react";

type OrderBadgeColor = NonNullable<BadgeProps["color"]>;

const useStyles = createStyles(() => ({
  badgeWrapper: {
    display: "inline-flex",
    alignItems: "center",
  },
  icon: {
    fontVariationSettings: "'FILL' 1",
  },
}));

/**
 * Maps Badge semantic colors to token color values for icons
 * Uses the same color shades as the Badge component's styling
 */
const getOrderBadgeIconColor = (badgeColor: OrderBadgeColor): string => {
  const colorMap: Record<OrderBadgeColor, string> = {
    blue: tokens.color.primitive.light.blue[700],
    purple: tokens.color.primitive.light.purple[700],
    green: tokens.color.primitive.light.green[700],
    orange: tokens.color.primitive.light.yellow[800],
    red: tokens.color.primitive.light.red[700],
    white: tokens.color.primitive.light.gray[900],
    gray: tokens.color.primitive.light.gray[700],
  };

  return colorMap[badgeColor];
};

const PublicStatusColor = {
  RECEIVED: "blue" as const,
  VALIDATING: "orange" as const,
  SHIPPED: "green" as const,
  CANCELLED: "white" as const,
  FAILED: "red" as const,
};

type PublicOrderStatusConfig = Record<
  GraphQL.PublicOrderStatus,
  {
    name: string;
    color: OrderBadgeColor;
  }
>;

const publicOrderStatusConfig: PublicOrderStatusConfig = {
  [GraphQL.PublicOrderStatus.Received]: {
    name: "Received",
    color: PublicStatusColor.RECEIVED,
  },
  [GraphQL.PublicOrderStatus.Validating]: {
    name: "Validating",
    color: PublicStatusColor.VALIDATING,
  },
  [GraphQL.PublicOrderStatus.ValidHoldingBin]: {
    name: "Valid - Holding Bin",
    color: PublicStatusColor.VALIDATING,
  },
  [GraphQL.PublicOrderStatus.ValidAwaitingPayment]: {
    name: "Valid - Awaiting Payment",
    color: PublicStatusColor.VALIDATING,
  },
  [GraphQL.PublicOrderStatus.Printing]: {
    name: "Submitted to Printer",
    color: PublicStatusColor.RECEIVED,
  },
  [GraphQL.PublicOrderStatus.ReceivedByPrinter]: {
    name: "Received by Printer",
    color: PublicStatusColor.RECEIVED,
  },
  [GraphQL.PublicOrderStatus.InProduction]: {
    name: "In Production",
    color: PublicStatusColor.RECEIVED,
  },
  [GraphQL.PublicOrderStatus.ReadyToShip]: {
    name: "Ready to Ship",
    color: PublicStatusColor.RECEIVED,
  },
  [GraphQL.PublicOrderStatus.Shipped]: {
    name: "Shipped",
    color: PublicStatusColor.SHIPPED,
  },
  [GraphQL.PublicOrderStatus.Failed]: {
    name: "Failed",
    color: PublicStatusColor.FAILED,
  },
  [GraphQL.PublicOrderStatus.Cancelled]: {
    name: "Cancelled",
    color: PublicStatusColor.CANCELLED,
  },
  [GraphQL.PublicOrderStatus.CancellationRequested]: {
    name: "Cancellation Requested",
    color: PublicStatusColor.VALIDATING,
  },
  [GraphQL.PublicOrderStatus.Exception]: {
    name: "Exception",
    color: PublicStatusColor.FAILED,
  },
  [GraphQL.PublicOrderStatus.Queued]: {
    name: "Queued",
    color: PublicStatusColor.RECEIVED,
  },
  [GraphQL.PublicOrderStatus.Rejected]: {
    name: "Rejected",
    color: PublicStatusColor.FAILED,
  },
};

interface OrderStatusCellProps {
  status?: GraphQL.PublicOrderStatus | null;
}

export const OrderStatusCell: React.FC<OrderStatusCellProps> = ({ status }) => {
  const { classes } = useStyles();
  if (!status) return <></>;
  const config = publicOrderStatusConfig[status];
  const iconColor = getOrderBadgeIconColor(config.color);

  return (
    <Box className={classes.badgeWrapper}>
      <Badge
        label={config.name}
        color={config.color}
        variant="round"
        size="small"
        border={true}
        icon={<IconV3 icon="circle" size={12} color={iconColor} className={classes.icon} />}
      />
    </Box>
  );
};
